import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faUser } from "@fortawesome/pro-regular-svg-icons";

import {
  FieldDefinition,
  FieldsOption,
} from "src/base-components/FilterPopover/types";
import { SELECT_DIVIDER } from "src/base-components/Select";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useQueueColumnSchemas } from "src/cases/queues/hooks/useQueueColumnSchemas";
import {
  buildStatusPairOptions,
  STATUS_FIELD_KEY,
} from "src/cases/queues/statusFilterTransform";
import {
  CaseQueueColumnKey,
  CaseQueueColumnSchema,
  CaseQueueColumnSchemas,
} from "src/cases/queues/types";
import { PillVariants } from "src/design-system/Pill";
import { TYPE_ICONS } from "src/utils/constants";

const BUILTIN_FILTER_FIELDS: FieldDefinition[] = [
  {
    key: "assignee_id",
    value: "Assignee",
    type: "assignee",
    icon: faUser,
  },
  {
    key: "created_at",
    value: "Created At",
    type: "datetime",
    icon: TYPE_ICONS.datetime,
  },
  {
    key: "updated_at",
    value: "Updated At",
    type: "datetime",
    icon: TYPE_ICONS.datetime,
  },
];

const mapColumnSchemaToFieldDefinition = (
  _key: CaseQueueColumnKey,
  schema: CaseQueueColumnSchema,
  icon: IconProp,
): FieldDefinition => {
  const meta =
    schema.type === "entity_ref"
      ? { entityType: schema.entity_type }
      : schema.type === "event_ref"
        ? { eventType: schema.event_type }
        : undefined;

  const base: FieldDefinition = {
    key: `properties.${schema.__key}+${schema.type}`,
    value: schema.display_name,
    type: schema.type,
    icon,
    meta,
  };

  if (schema.type === "enum" || schema.type === "tags") {
    return {
      ...base,
      options:
        base.key === STATUS_FIELD_KEY
          ? buildStatusPairOptions(schema.options)
          : schema.options.map((option) => ({
              key: option.value,
              value: option.displayName || option.value,
              pillVariant: option.color as PillVariants | undefined,
            })),
    };
  }

  return base;
};

const mapColumnSchemasToFieldDefinitions = (
  schemas: CaseQueueColumnSchemas,
  resolveIcon: (schema: CaseQueueColumnSchema) => IconProp,
): FieldDefinition[] => {
  return (
    Object.entries(schemas) as [CaseQueueColumnKey, CaseQueueColumnSchema][]
  )
    .filter(
      (entry): entry is [CaseQueueColumnKey, CaseQueueColumnSchema] =>
        entry[1] !== undefined,
    )
    .map(([key, schema]) =>
      mapColumnSchemaToFieldDefinition(key, schema, resolveIcon(schema)),
    );
};

type UseFilterFieldsResult = {
  fields: FieldsOption[];
  isLoading: boolean;
};

export const useFilterFields = (
  queueId: string | undefined,
): UseFilterFieldsResult => {
  const { columnSchemas, isLoading: isLoadingSchemas } =
    useQueueColumnSchemas(queueId);

  const getCasePropertyIcon = useGetCasePropertyIcon();

  const templateFields = mapColumnSchemasToFieldDefinitions(
    columnSchemas,
    getCasePropertyIcon,
  );

  const fields = [...BUILTIN_FILTER_FIELDS, SELECT_DIVIDER, ...templateFields];

  return { fields, isLoading: isLoadingSchemas };
};
